import { z } from "zod";

import { createQueryRequestDefinition } from "~/lib/network/definitions/create";
import { POLLING_TIMING_OPTIONS, PollingFailedError } from "~/lib/network/polling-helper";
import { queryClient } from "~/lib/query-client";

import {
  pollAudioClipRangesResponseSchema,
  listAuditionsForAudioResponseSchema,
  pollAuditionResponseSchema,
  getProjectDetailsResponseSchema,
  listProjectVideosResponseSchema,
  getVideoDetailsResponseSchema,
  videoGenerationStatusResponseSchema,
  clipRegenerationPollingResponseSchema,
  listProjectsResponseSchema,
  AuditionFromAudioVideoItemType,
  pollAuditionRequestSchema,
  PollAuditionRequestType,
  VideoGenerationStatusType,
  videoGenerationStatusRequestSchema,
  VideoGenerationStatusRequestType,
} from "./schemas";

export const listProjectsQuery = createQueryRequestDefinition({
  queryKey: ["projects", "list"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/projects/list",
  responseSchema: listProjectsResponseSchema,
  paramsSchema: z
    .object({ afterCursor: z.string().optional(), pageSize: z.number().optional() })
    .optional(),
}).withPagination({
  getNextPageParam(lastPage) {
    return lastPage.projects.pageInfo.hasNextPage ? lastPage.projects.pageInfo.endCursor : null;
  },
  mapPaginationParamsToQueryParams({ pageParam }) {
    return {
      ...(pageParam ? { afterCursor: pageParam } : {}),
      pageSize: 20,
    };
  },
});

export const getProjectDetailsQuery = createQueryRequestDefinition({
  queryKey: ["project", "details"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/project/metadata",
  paramsSchema: z.object({ id: z.string() }),
  responseSchema: getProjectDetailsResponseSchema,
});

export const pollAudioClipRangesCreationQuery = createQueryRequestDefinition({
  queryKey: ["audio", "clip-ranges", "poll"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/audio/clip-ranges/create/poll",
  responseSchema: pollAudioClipRangesResponseSchema,
  paramsSchema: z.object({ operationId: z.string() }),
  analytics: {
    level: "custom",
    shouldLogRequest(status, _, response) {
      // logs failures and non-pending states
      if (status === "cancel") {
        return false;
      }

      return status === "fail" || response?.state !== "PROCESSING";
    },
  },
}).withPolling({
  ...POLLING_TIMING_OPTIONS.long,
  determinePollingStatus(result, resultCreator) {
    if (result.state === "COMPLETE") {
      return resultCreator.done(result);
    }

    if (result.state === "FAILED") {
      return resultCreator.failed(result);
    }

    return resultCreator.pending(result);
  },
  onSuccess() {
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });
  },
});

export const listAuditionsForAudioQuery = createQueryRequestDefinition({
  queryKey: ["auditions", "list-for-audio"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/auditions/list-for-audio",
  paramsSchema: z.object({
    audioId: z.string(),
    projectId: z.string(),
  }),
  responseSchema: listAuditionsForAudioResponseSchema,
});

export const pollAuditionQuery = createQueryRequestDefinition({
  queryKey: ["audition", "poll"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/audition/poll",
  paramsSchema: pollAuditionRequestSchema,
  responseSchema: pollAuditionResponseSchema,
  analytics: {
    level: "custom",
    shouldLogRequest(status, _, response) {
      // logs failures and non-pending states
      if (status === "cancel") {
        return false;
      }

      return (
        status === "fail" ||
        (response?.video.state !== "PROCESSING" && response?.video.state !== "QUEUED")
      );
    },
  },
}).withPolling({
  ...POLLING_TIMING_OPTIONS.long,
  swallowAbortErrors: true,
  determinePollingStatus(result, resultCreator) {
    if (result.video.state === "COMPLETE" || result.video.state === "CANCELLED") {
      return resultCreator.done(result.video);
    }

    if (result.video.state === "FAILED") {
      return resultCreator.failed(result.video);
    }

    return resultCreator.pending(result.video);
  },
  onPollingPendingResult(result, variables) {
    updateAuditionVideoInCache(result, variables.params);
  },
  onSuccess(data, variables) {
    updateAuditionVideoInCache(data, variables.params);
  },
  onError(error, variables) {
    if (error instanceof PollingFailedError) {
      updateAuditionVideoInCache(error.result, variables.params);
    }
  },
});
export type AuditionPollingStatus = Awaited<
  ReturnType<typeof pollAuditionQuery.usePollingQuery>
>["currentResult"];

export const listProjectVideosQuery = createQueryRequestDefinition({
  queryKey: ["project", "videos"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/projects/videos/list",
  paramsSchema: z.object({
    projectId: z.string(),
    pageSize: z.number(),
    afterCursor: z.string().optional(),
  }),
  responseSchema: listProjectVideosResponseSchema,
}).withPagination({
  getNextPageParam(lastPage) {
    return lastPage.videos.pageInfo.hasNextPage ? lastPage.videos.pageInfo.endCursor : undefined;
  },
  mapPaginationParamsToQueryParams({ pageParam }) {
    return {
      ...(pageParam ? { afterCursor: pageParam } : {}),
      pageSize: 20,
    };
  },
});

export const getVideoDetailsQuery = createQueryRequestDefinition({
  queryKey: ["video", "details"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/video",
  paramsSchema: z.object({ videoId: z.string(), projectId: z.string() }),
  responseSchema: getVideoDetailsResponseSchema,
});

export const getVideoGenerationStatusQuery = createQueryRequestDefinition({
  queryKey: ["video", "generation", "status"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/video/generate/from-audition/poll",
  paramsSchema: videoGenerationStatusRequestSchema,
  responseSchema: videoGenerationStatusResponseSchema,
  analytics: {
    level: "custom",
    shouldLogRequest(status, _, response) {
      // logs failures and non-pending states
      if (status === "cancel") {
        return false;
      }

      return (
        status === "fail" || (response?.state !== "PROCESSING" && response?.state !== "QUEUED")
      );
    },
  },
}).withPolling({
  ...POLLING_TIMING_OPTIONS.long,
  swallowAbortErrors: true,
  determinePollingStatus(result, resultCreator) {
    if (result.state === "COMPLETE" || result.state === "CANCELLED") {
      return resultCreator.done(result);
    } else if (result.state === "FAILED") {
      return resultCreator.failed(result);
    }

    return resultCreator.pending(result);
  },
  onPollingPendingResult(result, variables) {
    updateProjectVideoInCache(result, variables.params);
  },
  onSuccess(data, variables) {
    updateProjectVideoInCache(data, variables.params);
  },
  onError(error, variables) {
    if (error instanceof PollingFailedError) {
      updateProjectVideoInCache(error.result, variables.params);
    }
  },
});

export type VideoDetailsPollingStatus = Awaited<
  ReturnType<typeof getVideoGenerationStatusQuery.usePollingQuery>
>["currentResult"];

export const pollVideoClipRegenerateQuery = createQueryRequestDefinition({
  queryKey: ["video", "clip", "regenerate", "poll"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/video/clip/regenerate/poll",
  paramsSchema: z.object({
    videoId: z.string(),
    clipId: z.string(),
  }),
  responseSchema: clipRegenerationPollingResponseSchema,
  analytics: {
    level: "custom",
    shouldLogRequest(status, _, response) {
      // logs failures and non-pending states
      if (status === "cancel") {
        return false;
      }

      return (
        status === "fail" || (response?.state !== "PROCESSING" && response?.state !== "QUEUED")
      );
    },
  },
}).withPolling({
  ...POLLING_TIMING_OPTIONS.long,
  swallowAbortErrors: true,
  determinePollingStatus(result, resultCreator) {
    if (result.state === "COMPLETE") {
      return resultCreator.done(result);
    } else if (result.state === "FAILED") {
      return resultCreator.failed(result);
    }

    return resultCreator.pending(result);
  },
});

export type VideoClipRegeneratePollingStatus = Awaited<
  ReturnType<typeof pollVideoClipRegenerateQuery.usePollingQuery>
>["currentResult"];

// optimistic updates

function updateAuditionVideoInCache(
  video: AuditionFromAudioVideoItemType,
  { auditionOperationId, audioId, projectId }: PollAuditionRequestType
) {
  listAuditionsForAudioQuery.setQueryData(
    queryClient,
    { params: { projectId, audioId } },
    (old) => {
      if (!old) {
        return old;
      }

      return {
        ...old,
        auditions: old.auditions.map((oldAudition) => {
          if (oldAudition.id === auditionOperationId) {
            return {
              ...oldAudition,
              video,
            };
          }

          return oldAudition;
        }),
      };
    }
  );
}

function updateProjectVideoInCache(
  video: VideoGenerationStatusType,
  { projectId }: VideoGenerationStatusRequestType
) {
  listProjectVideosQuery.updateItemsInQueryData(
    queryClient,
    { params: { projectId } },
    "videos",
    (oldVideo) => {
      if (oldVideo.id === video.id) {
        return video;
      }

      return oldVideo;
    }
  );
}
